"use client";

import { ReactNode } from "react";

import { addToast } from "@heroui/toast";

/**
 * Custom hook that provides toast functionality using HeroUI Toast
 * This hook wraps HeroUI's addToast to provide a similar API to Sonner
 */
interface ToastOptions {
  title?: string;
  description?: string | ReactNode;
}

type SuccessOptions = ToastOptions & { useOrgColor?: boolean };

function resolveToastDescription(
  message: string,
  options: ToastOptions | undefined,
): string | ReactNode {
  if (
    options &&
    "description" in options &&
    options.description !== undefined
  ) {
    return options.description;
  }

  return message;
}

export function useToast() {
  const success = (message: string, options?: SuccessOptions) => {
    return addToast({
      color: options && options.useOrgColor ? "primary" : "success",
      description: resolveToastDescription(message, options),
      title: (options && options.title) ?? "Success",
    });
  };

  const error = (message: string, options?: ToastOptions) => {
    return addToast({
      color: "danger",
      description: resolveToastDescription(message, options),
      title: options?.title ?? "Error",
    });
  };

  const warning = (message: string, options?: ToastOptions) => {
    return addToast({
      color: "warning",
      description: resolveToastDescription(message, options),
      title: options?.title ?? "Warning",
    });
  };

  const info = (message: string, options?: ToastOptions) => {
    return addToast({
      color: "primary",
      description: resolveToastDescription(message, options),
      title: options?.title ?? "Info",
    });
  };

  const toast = (message: string, options?: ToastOptions) => {
    return addToast({
      color: "default",
      description: resolveToastDescription(message, options),
      title: options?.title,
    });
  };

  // For backward compatibility with direct function calls
  toast.success = success;
  toast.error = error;
  toast.warning = warning;
  toast.info = info;

  return {
    error,
    info,
    success,
    toast,
    warning,
  };
}
